/**
 * @module emojis/ui/emojigridview
 */
import { View, ButtonView, addKeyboardHandlingForGrid } from 'ckeditor5/src/ui';
import { KeystrokeHandler, FocusTracker, global } from 'ckeditor5/src/utils';
import '../theme/emojigrid.css';
/**
 * A grid of emoji tiles. It allows browsing emojis and selecting the emoji to
 * be inserted into the content.
 */
export default class EmojiGridView extends View {
    /**
     * Creates an instance of a emoji grid containing tiles representing emojis.
     *
     * @param locale The localization services instance.
     */
    constructor(locale) {
        super(locale);
        this.tiles = this.createCollection();
        this.setTemplate({
            tag: 'div',
            children: [
                {
                    tag: 'div',
                    attributes: {
                        class: [
                            'ck',
                            'ck-emoji-grid__tiles'
                        ]
                    },
                    children: this.tiles
                }
            ],
            attributes: {
                class: [
                    'ck',
                    'ck-emoji-grid'
                ]
            }
        });
        this.focusTracker = new FocusTracker();
        this.keystrokes = new KeystrokeHandler();
        addKeyboardHandlingForGrid({
            keystrokeHandler: this.keystrokes,
            focusTracker: this.focusTracker,
            gridItems: this.tiles,
            numberOfColumns: () => global.window
                .getComputedStyle(this.element.firstChild) // Responsive .ck-emoji-grid__tiles
                .getPropertyValue('grid-template-columns')
                .split(' ')
                .length,
            uiLanguageDirection: this.locale && this.locale.uiLanguageDirection
        });
    }
    /**
     * Creates a new tile for the grid.
     *
     * @param emoji A human-readable emoji displayed as the label (e.g. "ε").
     * @param name The name of the emoji (e.g. "greek small letter epsilon").
     */
    createTile(emoji, name) {
        const tile = new ButtonView(this.locale);
        tile.set({
            label: emoji,
            withText: true,
            class: 'ck-emoji-grid__tile'
        });
        // Labels are vital for the users to understand what emoji they're looking at.
        // For now we're using native title attribute for that, see #5817.
        tile.extendTemplate({
            attributes: {
                title: name
            },
            on: {
                mouseover: tile.bindTemplate.to('mouseover'),
                focus: tile.bindTemplate.to('focus')
            }
        });
        tile.on('mouseover', () => {
            this.fire('tileHover', { name, emoji });
        });
        tile.on('focus', () => {
            this.fire('tileFocus', { name, emoji });
        });
        tile.on('execute', () => {
            this.fire('execute', { name, emoji });
        });
        return tile;
    }
    /**
     * @inheritDoc
     */
    render() {
        super.render();
        for (const item of this.tiles) {
            this.focusTracker.add(item.element);
        }
        this.tiles.on('change', (eventInfo, { added, removed }) => {
            if (added.length > 0) {
                for (const item of added) {
                    this.focusTracker.add(item.element);
                }
            }
            if (removed.length > 0) {
                for (const item of removed) {
                    this.focusTracker.remove(item.element);
                }
            }
        });
        this.keystrokes.listenTo(this.element);
    }
    /**
     * @inheritDoc
     */
    destroy() {
        super.destroy();
        this.keystrokes.destroy();
    }
    /**
     * Focuses the first focusable in {@link ~EmojiGridView#tiles}.
     */
    focus() {
        this.tiles.first.focus();
    }
}
